import { Container, Row, Col, Form, Card } from 'react-bootstrap';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sparkle from '../../../../components/Sparkle/Sparkle';
import { useNavigate } from 'react-router-dom';
import { RiSparklingFill } from '@remixicon/react';
import SpinnerButton from '../../../../components/Button/SpinnerButton';
import { fakeResponse } from '../../../../utils/helpers.js';
import { authFetch } from '../../../../utils/apiClient.js';
import { mockLegal } from '../../../../utils/mockData';
import useLanguageStore from '../../../../stores/languageStore.js';
import AccessModal from '../../../../components/Modal/AccessModal.jsx';
import useAccessType from '../../../../hooks/useAccessType.js';
import DemoModal from '../../../../components/Modal/DemoModal.jsx';

function TermsLegalScreen() {
    const { t } = useTranslation();
    const { language } = useLanguageStore();
    const [formData, setFormData] = useState({
        companyName: '',
        country: '',
        address: '',
        legalForm: '',
        publicationManager: '',
        hostName: '',
        hostAddress: '',
        activity: '',
        websiteLink: '',
        cookiesPolicyLink: '',
        privacyPolicyLink: '',
        contactMethod: '',
        language: '',
    });
    const [validated, setValidated] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [modalType, setModalType] = useState(null);
    const { getAccessType, isDemo } = useAccessType(true);
    const [showModal, setShowModal] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    async function generateLegal(legalData) {
        try {
            const res = await authFetch("/api/terms/generate-legal-notice", {
                method: "POST",
                body: JSON.stringify(legalData),
            });

            return await res.json()
        } catch (error) {
            console.error("Error generating legal:", error);
            return null;
        }
    }

    async function generateFakeLegal() {
        return fakeResponse(mockLegal)
    }

    const handleDemo = async () => {
        setIsLoading(true);
        const data = await generateFakeLegal();
        navigate('/terms/legal/generated', { state: { legal: data } });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const form = e.currentTarget;

        if (form.checkValidity() === false) {
            setValidated(true);
            return;
        }

        setValidated(false);

        if (isDemo) {
            setShowModal(true);
            return;
        }
        const accessType = getAccessType();
        if (accessType) {
            setModalType(accessType);
            setShowModal(true);
            return;
        }

        setIsLoading(true)
        const data = await generateLegal(formData);
        navigate('/terms/legal/generated', { state: { legal: data } });
    };

    const legalFormOptions = language === 'en' ? [
        { value: 'Sole Proprietorship', label: 'Sole Proprietorship' },
        { value: 'LLC', label: 'LLC (Limited Liability Company)' },
        { value: 'Corporation', label: 'Corporation (Inc.)' },
    ] : [
        { value: 'EI', label: 'EI (Entreprise individuelle)' },
        { value: 'EURL', label: 'EURL (Entreprise unipersonnelle à responsabilité limitée)' },
        { value: 'SAS', label: 'SAS (Société par actions simplifiée)' },
        { value: 'SARL', label: 'SARL (Société à responsabilité limitée)' },
        { value: 'SA', label: 'SA (Société anonyme)' },
        { value: 'SASU', label: 'SASU (Société par actions simplifiée unipersonnelle)' },
    ];

    return (
        <>
            {isLoading ? (
                <Sparkle />
            ) : (
                <Container className="py-5">
                    <Row className="mb-5">
                        <Col xs={12} className="text-center">
                            <h2 className="title">{t('legal.terms')}</h2>
                        </Col>
                    </Row>

                    <Card>
                        <Card.Body>
                            <Form noValidate validated={validated} onSubmit={handleSubmit} id="legalForm">
                                <Row className="mb-4 g-4">
                                    <Col xs={12} md={6}>
                                        {/* Informations de l'entreprise */}
                                        <Form.Group controlId="companyName">
                                            <Form.Label>{t('legal.companyName')} *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="companyName"
                                                value={formData.companyName}
                                                onChange={handleChange}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('legal.companyNameRequired')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Group controlId="country">
                                            <Form.Label>{t('legal.country')} *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="country"
                                                value={formData.country}
                                                onChange={handleChange}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('legal.countryRequired')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-4 g-4">
                                    <Col xs={12}>
                                        <Form.Group controlId="language">
                                            <Form.Label>{t('app.language')} *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="language"
                                                value={formData.language}
                                                onChange={handleChange}
                                                placeholder="Français, English, Deutsch..."
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('legal.languageRequired')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-4 g-4">
                                    <Col xs={12}>
                                        <Form.Group controlId="address">
                                            <Form.Label>{t('legal.address')} *</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                name="address"
                                                value={formData.address}
                                                onChange={handleChange}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('legal.addressRequired')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mb-4 g-4">
                                    <Col xs={12} md={6}>
                                        <Form.Group controlId="legalForm">
                                            <Form.Label>{t('legal.legalForm')} *</Form.Label>
                                            <Form.Select
                                                name="legalForm"
                                                value={formData.legalForm}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">{t('legal.selectLegalForm')}</option>
                                                {legalFormOptions.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {t('legal.legalFormRequired')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col xs={12} md={6}>
                                        <Form.Group controlId="publicationManager">
                                            <Form.Label>{t('legal.publicationManager')} *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="publicationManager"
                                                value={formData.publicationManager}
                                                onChange={handleChange}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('legal.publicationManagerRequired')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Informations de l'hébergeur */}
                                <Row className="mb-4 g-4">
                                    <Col xs={12}>
                                        <Form.Group controlId="hostName">
                                            <Form.Label>{t('legal.hostName')} *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="hostName"
                                                value={formData.hostName}
                                                onChange={handleChange}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('legal.hostNameRequired')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-4 g-4">
                                    <Col xs={12}>
                                        <Form.Group controlId="hostAddress">
                                            <Form.Label>{t('legal.hostAddress')} *</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                name="hostAddress"
                                                value={formData.hostAddress}
                                                onChange={handleChange}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('legal.hostAddressRequired')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Autres informations */}
                                <Row className="mb-4 g-4">
                                    <Col xs={12}>
                                        <Form.Group controlId="activity">
                                            <Form.Label>{t('legal.activity')} *</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                name="activity"
                                                value={formData.activity}
                                                onChange={handleChange}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('legal.activityRequired')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mb-4 g-4">
                                    <Col xs={12} md={6}>
                                        <Form.Group controlId="websiteLink">
                                            <Form.Label>{t('legal.websiteLink')} *</Form.Label>
                                            <Form.Control
                                                type="url"
                                                name="websiteLink"
                                                value={formData.websiteLink}
                                                onChange={handleChange}
                                                placeholder="https://..."
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('legal.urlInvalid')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Group controlId="cookiesPolicyLink">
                                            <Form.Label>{t('legal.cookiesPolicyLink')}</Form.Label>
                                            <Form.Control
                                                type="url"
                                                name="cookiesPolicyLink"
                                                value={formData.cookiesPolicyLink}
                                                onChange={handleChange}
                                                placeholder="https://..."
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('legal.urlInvalid')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mb-4 g-4">
                                    <Col xs={12} md={6}>
                                        <Form.Group controlId="privacyPolicyLink">
                                            <Form.Label>{t('legal.privacyPolicyLink')}</Form.Label>
                                            <Form.Control
                                                type="url"
                                                name="privacyPolicyLink"
                                                value={formData.privacyPolicyLink}
                                                onChange={handleChange}
                                                placeholder="https://..."
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('legal.urlInvalid')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Group controlId="contactMethod">
                                            <Form.Label>{t('legal.contactMethod')} *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="contactMethod"
                                                value={formData.contactMethod}
                                                onChange={handleChange}
                                                placeholder="exemple@email.com"
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('legal.contactMethodRequired')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12} md={{ span: 6, offset: 6 }}>
                                        <SpinnerButton
                                            type="submit"
                                            className="w-100 bg-pink"
                                            disabled={isLoading}
                                            label={<>
                                                <span className="me-2">{t('app.generate')}</span>
                                                <RiSparklingFill size={20} />
                                            </>}
                                        />
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>

                    <AccessModal
                        show={showModal && !isDemo}
                        setShow={setShowModal}
                        modalType={modalType}
                    />

                    <DemoModal
                        show={showModal && isDemo}
                        setShow={setShowModal}
                        onAction={handleDemo}
                    />
                </Container>
            )}
        </>
    );
}

export default TermsLegalScreen;
